/* global waterdropAppApi, SubscribeForm */

class WdSubscribedDraw extends HTMLElement {
  static MIN_SPIN_DURATION = 1500
  static FAST_SPIN_SPEED = 512
  static DRAW_CODE_MESSAGES = {
    410: "You've used all your chances to win. Stay tuned for more giveaways!",
    411: "You're already subscribed! This offer is for new subscribers only."
  }

  constructor() {
    super()
    this.activity = null
    this.prizes = []
    this.cache = null
    this.isDrawing = false
    this.isLoaded = false
    this.currentRotation = 0
    this.fastSpinFrame = null
    this.fastSpinLastAt = 0
    this.recordRules = []
    this.hasSubscribeClickSent = false
    this.hasViewedEventSent = false
    this.viewObserver = null
    this.visitorId = ''
    this.isVerifying = false
    this.handleSubscribeSubmit = this.onSubscribeSubmit.bind(this)
  }

  get activityId() {
    return this.dataset.activityId || ''
  }

  get subscribedTag() {
    return this.dataset.subscribedTag || ''
  }

  get isLoggedIn() {
    return this.hasAttribute('data-logged-in')
  }

  get customerEmail() {
    return this.normalizeEmail(this.dataset.customerEmail || '')
  }

  get cacheKey() {
    return `WD_SUBSCRIBED_DRAW_${this.activityId}`
  }

  connectedCallback() {
    if (this.initialized) return
    this.initialized = true
    this.cacheDOM()
    this.bindEvents()
    this.ensureVisitorId()
    this.initViewTracking()
    this.initStateFromCache()
    this.loadActivity()
  }

  disconnectedCallback() {
    this.stopFastSpin()
    this.form?.removeEventListener('submit', this.handleSubscribeSubmit)
    if (this._boundHistoryClick && this.historyModal) {
      this.historyModal.removeEventListener('click', this._boundHistoryClick)
    }
    if (this.viewObserver) {
      this.viewObserver.disconnect()
      this.viewObserver = null
    }
  }

  cacheDOM() {
    this.wheelEl = this.querySelector('[data-wheel]')
    this.discEl = this.querySelector('[data-wheel-disc]')
    this.form = this.querySelector('[data-subscribe-form]')
    this.emailInput = this.querySelector('[data-email-input]')
    this.drawBtn = this.querySelector('[data-draw-button]')
    this.redrawBtn = this.querySelector('[data-redraw-button]')
    this.initialState = this.querySelector('[data-state-initial]')
    this.resultState = this.querySelector('[data-state-result]')
    this.rulesOpeners = this.querySelectorAll('[data-rules-opener], [data-result-rules-opener]')

    this.resultPrizeName = this.querySelector('[data-result-prize-name]')
    this.resultEmail = this.querySelector('[data-result-email]')
    this.resultPrizeImage = this.querySelector('[data-result-prize-image]')
    this.resultCardName = this.querySelector('[data-result-card-name]')
    this.resultCodeWrap = this.querySelector('[data-result-code-wrap]')
    this.prizeCard = this.querySelector('.wd-sd__prize-card')

    this.rulesModal = document.getElementById(this.dataset.rulesModalId)
    this.historyModal = document.getElementById(this.dataset.historyModalId)
    this.recordRuleModal = document.getElementById(this.dataset.recordRuleModalId)
    this.rulesBody = this.rulesModal?.querySelector('[data-rules-body]')
    this.historySummary = this.historyModal?.querySelector('[data-history-summary]')
    this.historyList = this.historyModal?.querySelector('[data-history-list]')
    this.historyItemTpl = this.historyModal?.querySelector('[data-history-item-tpl]')
    this.historyEmptyTpl = this.historyModal?.querySelector('[data-history-empty-tpl]')
    this.recordRuleBody = this.recordRuleModal?.querySelector('[data-record-rule-body]')
  }

  bindEvents() {
    this.form?.addEventListener('submit', this.handleSubscribeSubmit)
    this.redrawBtn?.addEventListener('click', () => this.handleDrawClick(false))

    this._boundHistoryClick = event => this.handleHistoryClick(event)
    this.historyModal?.addEventListener('click', this._boundHistoryClick)
    this.historyModal?.addEventListener('modal:show', () => this.renderHistory())
  }

  // 首屏状态只依赖登录邮箱和本地缓存；活动接口加载完成前按钮仍保持禁用。
  initStateFromCache() {
    if (this.emailInput && this.customerEmail) this.emailInput.value = this.customerEmail

    const cache = this.readCache()
    if (!cache) {
      this.showInitialState()
      return
    }

    if (this.isLoggedIn && cache.email !== this.customerEmail) {
      this.clearCache()
      this.showInitialState()
      return
    }

    this.cache = cache
    const latest = this.getLatestRecord(cache.records)
    if (latest) {
      this.showResultState(latest, cache.email, cache.remaining_draws)
    } else {
      this.showInitialState()
    }
  }

  async loadActivity() {
    if (!this.activityId) {
      this.notify('Activity ID is required')
      this.finishLoading(false)
      return
    }

    try {
      const raw = await waterdropAppApi({ proxy: 'rewards/api', endpoint: `/activities/${this.activityId}` })
      const payload = this.normalizeActivityResponse(raw)
      if (!payload) {
        this.notify('Failed to load activity. Please try again later.')
        this.finishLoading(false)
        return
      }

      this.activity = payload.activity
      this.prizes = Array.isArray(payload.prizes) ? payload.prizes : []
      this.renderWheel()
      this.renderRules()
      this.finishLoading(true)
    } catch (error) {
      this.notify(this.cleanErrorMessage(error) || 'Failed to load activity. Please try again later.')
      this.finishLoading(false)
    }
  }

  // 兼容 waterdropAppApi 返回后端信封或直接返回 data 的两种形态。
  normalizeActivityResponse(raw) {
    if (!raw) return null
    if (raw.code && raw.code !== 200) {
      this.notify(raw.message || 'Failed to load activity. Please try again later.')
      return null
    }
    if (raw.data?.activity) return raw.data
    if (raw.activity) return raw
    return null
  }

  finishLoading(success) {
    this.isLoaded = success
    this.classList.add('is-loaded')
    this.updateButtons()
  }

  renderRules() {
    const rulesHtml = this.activity?.rules_description || ''
    const hasRules = this.hasRichTextContent(rulesHtml)
    if (this.rulesBody) this.rulesBody.innerHTML = hasRules ? rulesHtml : ''
    this.rulesOpeners.forEach(opener => {
      opener.hidden = !hasRules
    })
  }

  // 每个奖品用带圆角的扇形 SVG。尖角对齐到转盘圆心，直边才能拼上，外圈圆角仍留着。
  renderWheel() {
    if (!this.discEl || !this.prizes.length) return

    const count = this.prizes.length
    const step = 360 / count
    const sector = this.getSectorTemplate(count)
    this.discEl.innerHTML = ''
    this.discEl.style.background = 'transparent'

    this.prizes.forEach((prize, index) => {
      const item = document.createElement('div')
      item.className = 'wd-sd__wheel-prize'
      item.style.setProperty('--angle', `${index * step}deg`)
      item.style.setProperty('--sector-ratio', String(sector.width / sector.height))
      item.style.setProperty('--image-size', `${sector.image}px`)

      const name = document.createElement('span')
      name.textContent = prize.prize_name || ''

      const content = document.createElement('div')
      content.className = 'wd-sd__prize-content'
      content.appendChild(name)
      if (prize.prize_image) {
        const img = document.createElement('img')
        img.loading = 'lazy'
        img.src = prize.prize_image
        img.alt = prize.prize_name || ''
        img.width = sector.image
        img.height = sector.image
        content.appendChild(img)
      }

      item.append(this.createSectorBackground(sector, index), content)
      this.discEl.appendChild(item)
    })
  }

  getSectorTemplate(count) {
    const templates = {
      4: {
        width: 236,
        height: 171,
        image: 70,
        apexX: 117.715,
        apexY: 169.286,
        viewBox: '0 0 236 171',
        path: 'M 3.837 55.408 C 1.010 52.581 1.000 47.986 3.946 45.283 C 34.989 16.802 75.586 1.000 117.715 1.000 C 159.845 1.000 200.442 16.802 231.485 45.283 C 234.430 47.986 234.420 52.581 231.593 55.408 L 117.715 169.286 Z'
      },
      5: {
        width: 203,
        height: 171,
        image: 64,
        apexX: 101.473,
        apexY: 169.286,
        viewBox: '0 0 203 171',
        path: 'M 3.433 41.518 C 1.000 38.347 1.589 33.789 4.863 31.494 C 33.169 11.647 66.902 1.000 101.473 1.000 C 136.045 1.000 169.777 11.647 198.084 31.494 C 201.357 33.789 201.947 38.347 199.513 41.518 L 101.473 169.286 Z'
      },
      6: {
        width: 164,
        height: 169,
        image: 58,
        apexX: 81.4937,
        apexY: 168.286,
        viewBox: '0 0 164 169',
        path: 'M0.969839 28.8144C-1.0289 25.3525 0.150709 20.9108 3.69537 19.0627C27.6893 6.55321 54.3811 -0.00277896 81.5113 5.17859e-05C108.641 0.00288825 135.332 6.56448 159.323 19.079C162.867 20.9278 164.046 25.3698 162.047 28.8313L81.4937 168.286L0.969839 28.8144Z'
      }
    }
    return templates[count] || templates[6]
  }

  createSectorBackground(sector, index) {
    const ns = 'http://www.w3.org/2000/svg'
    const wrap = document.createElement('div')
    wrap.className = 'wd-sd__sector-bg'

    const svg = document.createElementNS(ns, 'svg')
    svg.setAttribute('viewBox', sector.viewBox)
    svg.setAttribute('fill', 'none')
    svg.setAttribute('aria-hidden', 'true')
    const shiftX = 50 - (sector.apexX / sector.width) * 100
    const shiftY = 100 - (sector.apexY / sector.height) * 100
    svg.style.transform = `translate(${shiftX}%, ${shiftY}%)`

    const path = document.createElementNS(ns, 'path')
    const color = index % 2 === 0 ? 'var(--sd-prize-bg-1)' : 'var(--sd-prize-bg-2)'
    path.setAttribute('d', sector.path)
    path.style.fill = color
    path.style.stroke = color
    path.setAttribute('stroke-width', '1.5')
    path.setAttribute('stroke-linejoin', 'round')
    svg.appendChild(path)
    wrap.appendChild(svg)

    return wrap
  }

  onSubscribeSubmit(event) {
    event.preventDefault()
    event.stopImmediatePropagation()
    this.handleDrawClick(true)
  }

  async ensureVisitorId() {
    if (this.visitorId || typeof SubscribeForm === 'undefined') return

    try {
      const visitorId = await SubscribeForm.loadVisitorId()
      if (!this.isConnected || !visitorId) return
      this.visitorId = visitorId
    } catch (error) {
      console.error(error)
    }
  }

  protectCaptcha() {
    const protect = window.Shopify?.captcha?.protect
    if (!this.form || typeof protect !== 'function' || typeof SubscribeForm === 'undefined') return Promise.resolve()

    SubscribeForm.installSubmitBridge()

    return new Promise((resolve, reject) => {
      let settled = false
      let timeoutId = 0
      const settle = callback => {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        SubscribeForm.pendingSubmit.delete(this.form)
        callback()
      }

      timeoutId = window.setTimeout(() => {
        settle(() => reject(new Error('CAPTCHA timed out')))
      }, 120000)

      SubscribeForm.pendingSubmit.set(this.form, () => settle(resolve))

      try {
        protect(this.form, () => {
          const { submit } = this.form
          if (submit === HTMLFormElement.prototype.submit) {
            settle(() => reject(new Error('CAPTCHA did not bind')))
            return
          }
          submit.call(this.form)
        })
      } catch (error) {
        settle(() => reject(error))
      }
    })
  }

  async handleDrawClick(isInitialDraw = false) {
    if (this.isDrawing || this.isVerifying || !this.isLoaded) return

    const email = this.normalizeEmail(this.emailInput?.value || this.cache?.email || this.customerEmail)
    if (!this.isValidEmail(email)) {
      this.notify('Please enter a valid email address.')
      return
    }

    if (isInitialDraw) {
      this.isVerifying = true
      this.updateButtons()
      try {
        await this.ensureVisitorId()
        await this.protectCaptcha()
      } catch (error) {
        console.error(error)
        this.notify('Subscription failed. Please try again later.')
        return
      } finally {
        this.isVerifying = false
        this.updateButtons()
      }
      this.trackInitialSubscribeClick()
    } else {
      await this.ensureVisitorId()
    }

    const beforeRotation = this.currentRotation
    this.isDrawing = true
    this.updateButtons()
    this.startFastSpin()

    // 请求期间先快转，接口成功后再接减速动画；失败则恢复点击前角度。
    try {
      const response = await this.postSubscribedDraw(email, isInitialDraw)
      if (!response || response.code !== 200) {
        // 其他设备可能已经用完次数，失败体里的 remaining_draws 才是当前真实剩余。
        this.syncRemainingFromFailure(response)
        throw new Error(response?.message || 'Draw failed. Please try again.')
      }

      const data = response.data || {}
      if (isInitialDraw) this.trackSubscribeSuccess(data)
      this.trackLotteryCompleted(data)
      const targetIndex = this.findPrizeIndex(data)
      this.stopFastSpin()
      await this.animateToPrize(targetIndex)
      this.syncCache(email, data)
      this.showResultState(data, email, this.cache?.remaining_draws ?? 0, true)
    } catch (error) {
      this.stopFastSpin()
      this.setWheelRotation(beforeRotation)
      this.notify(this.cleanErrorMessage(error) || 'Draw failed. Please try again.')
    } finally {
      this.isDrawing = false
      this.updateButtons()
    }
  }

  captchaToken() {
    const field = this.form?.elements['h-captcha-response']
    return typeof field?.value === 'string' ? field.value : ''
  }

  async postSubscribedDraw(email, includeCaptcha = false) {
    const payload = { email, id: this.activityId }
    if (this.subscribedTag) payload.tag = this.subscribedTag
    const captchaResponse = includeCaptcha ? this.captchaToken() : ''
    if (captchaResponse) payload['h-captcha-response'] = captchaResponse

    const headers = { 'Content-Type': 'application/json' }
    if (this.visitorId) headers['X-Visitor-Id'] = this.visitorId
    const res = await fetch('/apps/waterdrop/subscription_draws', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    })

    let json = null
    try {
      json = await res.json()
    } catch {
      json = null
    }

    const code = !res.ok ? (typeof json?.code === 'number' ? json.code : res.status) : json?.code
    const mappedMessage = WdSubscribedDraw.DRAW_CODE_MESSAGES[Number(code)]
    if (!res.ok || mappedMessage) {
      return {
        ...(json || {}),
        code,
        message: mappedMessage || json?.message || 'Draw failed. Please try again.'
      }
    }

    return json
  }

  // 次数用尽时接口不返回中奖结果，但 data.remaining_draws 为 0。写回缓存后按钮才会置灰。
  syncRemainingFromFailure(response) {
    if (!this.cache) return

    const raw = response?.data?.remaining_draws
    const hasReported = raw !== undefined && raw !== null && raw !== '' && Number.isFinite(Number(raw))
    const exhausted = Number(response?.code) === 410
    if (!hasReported && !exhausted) return

    const remaining = exhausted ? 0 : Math.max(0, Number(raw))
    if (this.cache.remaining_draws === remaining) return

    this.cache = { ...this.cache, remaining_draws: remaining }
    this.writeCache(this.cache)
  }

  // 接口给完整记录列表时以接口为准；只给单次结果时追加到本地历史。
  syncCache(email, drawData) {
    const previousRecords = Array.isArray(this.cache?.records) ? this.cache.records : []
    const returnedRecords = this.getReturnedRecords(drawData)
    const records = returnedRecords || previousRecords.concat(drawData)
    const remainingDraws = Number(drawData.remaining_draws ?? this.cache?.remaining_draws ?? 0)

    this.cache = {
      email,
      remaining_draws: Number.isFinite(remainingDraws) ? remainingDraws : 0,
      records
    }
    this.writeCache(this.cache)
  }

  getReturnedRecords(data) {
    const list = data?.records || data?.draw_records || data?.list
    return Array.isArray(list) ? list : null
  }

  showInitialState() {
    this.initialState?.classList.add('is-active')
    this.resultState?.classList.remove('is-active')
    this.updateButtons()
  }

  showResultState(record, email, remainingDraws, popCard = false) {
    if (!record) {
      this.showInitialState()
      return
    }

    this.initialState?.classList.remove('is-active')
    this.resultState?.classList.toggle('is-instant', popCard)
    this.resultState?.classList.add('is-active')

    const prizeName = record.prize_name || ''
    if (this.resultPrizeName) this.resultPrizeName.textContent = prizeName
    if (this.resultEmail) this.resultEmail.textContent = email || ''
    if (this.resultCardName) this.resultCardName.textContent = prizeName
    if (this.resultPrizeImage) {
      this.resultPrizeImage.src = record.prize_image || ''
      this.resultPrizeImage.alt = prizeName
    }

    this.updateCodeButton(this.resultCodeWrap, this.getPrizeCode(record))
    if (popCard) this.playPrizePop()

    if (!this.cache) {
      this.cache = {
        email,
        remaining_draws: Number(remainingDraws) || 0,
        records: [record]
      }
    }
    this.updateButtons()
  }

  // 结果区刚从 display:none 显示时，同一帧加动画类会被跳过，等到下一帧再播。
  playPrizePop() {
    if (!this.prizeCard) return
    this.prizeCard.classList.remove('is-pop')
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.prizeCard?.classList.add('is-pop')
      })
    })
  }

  // 结果态的按钮文案和禁用状态统一由剩余次数、加载态、转动态决定。
  updateButtons() {
    const canDraw = this.isLoaded && !this.isDrawing && !this.isVerifying
    if (this.drawBtn) this.drawBtn.disabled = !canDraw

    if (!this.redrawBtn) return
    const remaining = Number(this.cache?.remaining_draws || 0)
    const label = `Try Your Luck (${remaining} ${remaining === 1 ? 'chance' : 'chances'} left)`
    this.redrawBtn.textContent = label
    this.redrawBtn.disabled = !canDraw || remaining <= 0
  }

  startFastSpin() {
    this.stopFastSpin()
    this.fastSpinLastAt = performance.now()
    const tick = now => {
      const delta = now - this.fastSpinLastAt
      this.fastSpinLastAt = now
      this.setWheelRotation(this.currentRotation + (delta / 1000) * WdSubscribedDraw.FAST_SPIN_SPEED)
      this.fastSpinFrame = requestAnimationFrame(tick)
    }
    this.fastSpinFrame = requestAnimationFrame(tick)
  }

  stopFastSpin() {
    if (!this.fastSpinFrame) return
    cancelAnimationFrame(this.fastSpinFrame)
    this.fastSpinFrame = null
  }

  // 先沿快转速度走到减速起点，再用固定时长三次缓出停到指针。
  // 缓出路程 = 速度 × 时长 / 3，初速度正好等于快转，不会突然加速或卡住。
  animateToPrize(targetIndex) {
    return new Promise(resolve => {
      if (!this.prizes.length) {
        resolve()
        return
      }

      const speed = WdSubscribedDraw.FAST_SPIN_SPEED
      const duration = WdSubscribedDraw.MIN_SPIN_DURATION
      const distance = (speed * duration) / 3000
      const step = 360 / this.prizes.length
      const targetModulo = this.normalizeRotation(-targetIndex * step)
      const releaseModulo = this.normalizeRotation(targetModulo - distance)

      let phase = 'coast'
      let easeFrom = 0
      let easeStartedAt = 0
      let last = this.fastSpinLastAt || performance.now()

      const tick = now => {
        if (phase === 'coast') {
          const moved = ((now - last) / 1000) * speed
          const dist = this.normalizeRotation(releaseModulo - this.currentRotation)

          if (moved >= dist) {
            easeFrom = this.currentRotation + dist
            easeStartedAt = last + (dist / speed) * 1000
            phase = 'ease'
          } else {
            this.setWheelRotation(this.currentRotation + moved)
            last = now
          }
        }

        if (phase === 'ease') {
          const progress = Math.min(1, (now - easeStartedAt) / duration)
          const eased = 1 - Math.pow(1 - progress, 3)
          this.setWheelRotation(easeFrom + distance * eased)
          if (progress >= 1) {
            resolve()
            return
          }
        }

        requestAnimationFrame(tick)
      }

      requestAnimationFrame(tick)
    })
  }

  setWheelRotation(deg) {
    this.currentRotation = deg
    if (this.discEl) this.discEl.style.transform = `rotate(${deg}deg)`
  }

  findPrizeIndex(data) {
    const index = this.prizes.findIndex(prize => String(prize.prize_id) === String(data?.prize_id))
    return index >= 0 ? index : 0
  }

  // 历史弹窗每次打开都按当前缓存重渲染，避免复制码按钮复用旧 DOM 状态。
  renderHistory() {
    if (!this.historyList || !this.historyItemTpl) return

    const records = Array.isArray(this.cache?.records) ? this.cache.records : []
    if (this.historySummary) {
      this.historySummary.textContent = `You've won ${records.length} rewards in total. All rewards have been sent to ${
        this.cache?.email || this.customerEmail || ''
      }`
    }

    this.recordRules = []
    this.historyList.innerHTML = ''

    if (!records.length) {
      if (this.historyEmptyTpl) this.historyList.appendChild(this.historyEmptyTpl.content.cloneNode(true))
      return
    }

    const frag = document.createDocumentFragment()
    records.forEach((record, index) => {
      const item = this.historyItemTpl.content.cloneNode(true).firstElementChild
      const image = item.querySelector('[data-history-image]')
      const title = item.querySelector('[data-history-title]')
      const valid = item.querySelector('[data-history-valid]')
      const validText = item.querySelector('[data-history-valid-text]')
      const divider = item.querySelector('[data-history-divider]')
      const ruleBtn = item.querySelector('[data-history-rule]')

      if (image) {
        image.src = record.prize_image || ''
        image.alt = record.prize_name || ''
      }
      if (title) title.textContent = record.prize_name || ''

      const code = this.getPrizeCode(record)
      this.updateCodeButton(item.querySelector('[data-history-code-wrap]'), code)

      const validity = this.formatValidity(record)
      if (validity && valid && validText) {
        valid.hidden = false
        validText.textContent = validity
        if (divider && code) divider.hidden = false
      }

      if (ruleBtn && this.hasRichTextContent(record.winning_pop_up_rule)) {
        this.recordRules[index] = record.winning_pop_up_rule
        ruleBtn.hidden = false
        ruleBtn.dataset.recordIndex = String(index)
      }

      frag.appendChild(item)
    })

    this.historyList.appendChild(frag)
  }

  handleHistoryClick(event) {
    const btn = event.target.closest('[data-history-rule]')
    if (!btn) return

    const rule = this.recordRules[Number(btn.dataset.recordIndex)] || ''
    if (!this.hasRichTextContent(rule)) return
    if (this.recordRuleBody) this.recordRuleBody.innerHTML = rule
    this.recordRuleModal?.show()
  }

  // 复制功能复用全站 component-code-button，只更新 data-value 和展示文本。
  updateCodeButton(wrap, code) {
    if (!wrap) return
    const button = wrap.querySelector('.component-code-button')
    if (!button || !code) {
      wrap.hidden = true
      return
    }

    wrap.hidden = false
    button.dataset.value = code
    const codeText = button.querySelector('p')
    if (codeText) codeText.textContent = code
  }

  getPrizeCode(record) {
    if (!record || record.prize_type === 'NO_PRIZE') return ''
    return record.discount_code || record.gift_card_code || ''
  }

  readCache() {
    try {
      const raw = window.localStorage.getItem(this.cacheKey)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!this.isValidCache(parsed)) {
        this.clearCache()
        return null
      }
      return {
        email: this.normalizeEmail(parsed.email),
        remaining_draws: Number(parsed.remaining_draws),
        records: parsed.records
      }
    } catch {
      this.clearCache()
      return null
    }
  }

  writeCache(cache) {
    try {
      window.localStorage.setItem(this.cacheKey, JSON.stringify(cache))
    } catch {
      // localStorage can be unavailable in private browsing; the UI can still continue.
    }
  }

  clearCache() {
    try {
      window.localStorage.removeItem(this.cacheKey)
    } catch {
      // Ignore storage errors.
    }
  }

  isValidCache(value) {
    return (
      value &&
      typeof value.email === 'string' &&
      this.isValidEmail(this.normalizeEmail(value.email)) &&
      Number.isFinite(Number(value.remaining_draws)) &&
      Array.isArray(value.records)
    )
  }

  getLatestRecord(records) {
    if (!Array.isArray(records) || !records.length) return null
    return records[records.length - 1]
  }

  // 本地邮箱统一小写，保证登录邮箱和缓存邮箱比较稳定。
  normalizeEmail(email) {
    return String(email || '')
      .trim()
      .toLowerCase()
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  normalizeRotation(deg) {
    return ((deg % 360) + 360) % 360
  }

  formatValidity(record) {
    if (!record.discount_code_start && !record.discount_code_expiry) return ''
    const start = record.discount_code_start ? this.formatDateDay(record.discount_code_start) : ''
    const end = record.discount_code_expiry ? this.formatDateDay(record.discount_code_expiry) : ''
    return [start, end].filter(Boolean).join('-')
  }

  formatDateDay(value) {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  // 空富文本常见为 <p><br></p>，需要按可见文本判断是否展示入口。
  hasRichTextContent(html) {
    if (typeof html !== 'string') return false
    return (
      html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .trim().length > 0
    )
  }

  cleanErrorMessage(error) {
    const message = error?.message || ''
    return message.replace(/^API [^:]+:\s*/, '').trim()
  }

  notify(message) {
    if (typeof window.showMessage === 'function') {
      window.showMessage(message, 'error', 5000)
    }
  }

  // 板块进入视口一半后记一次曝光，避免首屏外的模块提前上报。
  initViewTracking() {
    if (this.hasViewedEventSent || this.viewObserver) return

    if (!('IntersectionObserver' in window)) {
      this.sendViewedEvent()
      return
    }

    this.viewObserver = new IntersectionObserver(
      entries => {
        const isVisible = entries.some(
          entry => entry.target === this && entry.isIntersecting && entry.intersectionRatio >= 0.5
        )
        if (!isVisible) return
        this.sendViewedEvent()
      },
      { threshold: 0.5 }
    )
    this.viewObserver.observe(this)
  }

  sendViewedEvent() {
    if (this.hasViewedEventSent || typeof window.WDSendEvent !== 'function') return
    window.WDSendEvent('wd_lottery_subscribe_viewed')
    this.hasViewedEventSent = true
    if (this.viewObserver) {
      this.viewObserver.disconnect()
      this.viewObserver = null
    }
  }

  // Enter & Spin 是首次订阅抽奖；再次点击或 Try Your Luck 不再记订阅点击。
  trackInitialSubscribeClick() {
    if (this.hasSubscribeClickSent || typeof window.WDSendEvent !== 'function') return
    this.hasSubscribeClickSent = true
    window.WDSendEvent('wd_email_subscribe_click', { event_source: this.subscribedTag })
  }

  trackSubscribeSuccess(data) {
    if (typeof window.WDSendEvent !== 'function') return
    window.WDSendEvent('wd_email_subscribe_success', {
      event_source: this.subscribedTag,
      event_label: data?.subscription_label
    })
  }

  trackLotteryCompleted(data) {
    if (typeof window.WDSendEvent !== 'function') return
    window.WDSendEvent('wd_lottery_subscribe_completed', {
      event_category: data?.prize_type,
      value: data?.prize_name
    })
  }
}

if (!customElements.get('wd-subscribed-draw')) {
  customElements.define('wd-subscribed-draw', WdSubscribedDraw)
}
